'use client'

import TabContext from '@mui/lab/TabContext'
import TabPanel from '@mui/lab/TabPanel'
import { Grid2, List, ListItem, ListItemButton, Paper, Typography } from '@mui/material'
import { ReactElement, ReactNode } from 'react'
import { tss } from 'tss-react/mui'
import InlineBadge from '../InlineBadge/InlineBadge'

const useStyles = tss.create(({ theme }) => ({
    tabPanel: {
        paddingTop: 0,
    },
    listItem: {
        height: 48,
    },
    listItemButton: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    paper: {
        overflow: 'hidden',
    },
}))

export type VerticalTabsProps = {
    tabs: {
        label: string
        value: string
        count?: number
        content: ReactNode
    }[]
    currentTab?: string
    onChangeTab: (newTab: string) => void
}

function VerticalTabs({ currentTab, tabs, onChangeTab }: VerticalTabsProps): ReactElement {
    const { classes } = useStyles()
    const { tabItems, tabPanels } = tabs.reduce(
        (acc: { tabItems: ReactNode[]; tabPanels: ReactNode[] }, tab) => {
            const { content, label, value, count } = tab
            const tabLabel = (
                <Typography variant="subtitle1" color="primary">
                    {label}
                </Typography>
            )
            acc.tabItems.push(
                <ListItem key={`tab-${value}`} divider className={classes.listItem} disablePadding>
                    <ListItemButton
                        selected={currentTab === value}
                        onClick={() => onChangeTab(value)}
                        aria-selected={currentTab === value}
                        className={classes.listItemButton}
                    >
                        {count ? <InlineBadge badgeContent={count}>{tabLabel}</InlineBadge> : tabLabel}
                    </ListItemButton>
                </ListItem>
            )
            acc.tabPanels.push(
                <TabPanel key={value} value={value} className={classes.tabPanel}>
                    {content}
                </TabPanel>
            )

            return acc
        },
        { tabItems: [], tabPanels: [] }
    )

    return (
        <TabContext value={currentTab ?? ''}>
            <Grid2 container spacing={4}>
                <Grid2 size={{ xs: 12, sm: 4 }}>
                    <Paper className={classes.paper}>
                        <List disablePadding>{tabItems}</List>
                    </Paper>
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 8 }}>{tabPanels}</Grid2>
            </Grid2>
        </TabContext>
    )
}

export default VerticalTabs
